"use client";

import Image from "next/image";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";

// ── Motion ────────────────────────────────────────────────────────────────────

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.06 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.2, 0, 0, 1] },
    },
  };
}

const headlineStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.1 } },
};

function revealLine(reduce: boolean): Variants {
  return {
    hidden: {
      opacity: 0,
      y: reduce ? 0 : "112%",
      filter: reduce ? "none" : "blur(8px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.78, ease: [0.2, 0, 0, 1] },
    },
  };
}

// ── Section ───────────────────────────────────────────────────────────────────

export function AboutHero() {
  const reduce = useReducedMotion() ?? false;

  return (
    <section
      className="relative w-full overflow-hidden bg-neutral-0"
      aria-labelledby="about-hero-heading"
    >
      {/* Decorative background image, faint */}
      <Image
        src="/hero-bg.png"
        alt=""
        width={1975}
        height={796}
        priority
        className="pointer-events-none absolute inset-x-0 top-0 h-full w-full select-none object-cover object-top opacity-40"
        aria-hidden="true"
      />
      {/* Soft radial primary tint */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_50%_-10%,rgba(14,58,138,0.10),transparent_60%)]"
        aria-hidden="true"
      />
      {/* Bottom fade to white so the next band blends */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[120px] bg-gradient-to-b from-transparent to-neutral-0"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto max-w-[1440px] px-6 pb-16 pt-16 sm:px-10 sm:pb-20 sm:pt-20 lg:px-20 lg:pb-24 lg:pt-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="mx-auto flex max-w-[840px] flex-col items-center text-center"
        >
          {/* Eyebrow */}
          <motion.p
            variants={fadeUp(reduce)}
            className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary-100 bg-white/70 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-primary-500 backdrop-blur-sm"
          >
            Our Story
          </motion.p>

          {/* Headline */}
          <motion.h1
            id="about-hero-heading"
            variants={headlineStagger}
            className="text-[clamp(2.15rem,4.6vw,3.5rem)] font-semibold leading-[1.08] tracking-[-0.015em] text-primary-800"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span variants={revealLine(reduce)} className="block">
                Real gold ownership,
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span variants={revealLine(reduce)} className="block">
                made{" "}
                <span className="font-instrument italic font-normal">
                  simple
                </span>{" "}
                for everyone.
              </motion.span>
            </span>
          </motion.h1>

          {/* Subcopy */}
          <motion.p
            variants={fadeUp(reduce)}
            className="mt-6 max-w-[620px] text-lg leading-relaxed text-text-body"
          >
            JustGold is the UAE&apos;s digital gold platform. We help anyone buy,
            save, and own real 24K gold with the security of a vault and the ease
            of an app.
          </motion.p>

          {/* Tagline chip */}
          <motion.p
            variants={fadeUp(reduce)}
            className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-[#e3ecfb] bg-white px-5 py-2.5 text-sm font-medium text-primary-800 shadow-[0_4px_20px_rgba(14,58,138,0.06)]"
          >
            <span
              className="h-1.5 w-1.5 rounded-full bg-accent-500"
              aria-hidden="true"
            />
            trust, backed by gold.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
}
